import React, { useState, useRef, useEffect } from "react";
import {
  Navigate,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/functions";
import { useAuth } from "hooks/useAuth";
import useMfaStatus from "hooks/useMfaStatus";
import Loading from "components/Loading";
import AuthFlow, { STEPS } from "components/auth/AuthFlow";
import InstitutionSelector from "components/auth/InstitutionSelector";
import logoIcon from "assets/images/logos/logo-icon.png";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import {
  hasFacilityCardClaim,
  needsMfaEnrollment,
} from "utils/needsMfaEnrollment";
import { sanitizeInAppReturnPath } from "features/carestart/utils/sanitizeInAppReturnPath";
import { getPlatformBranding } from "config/platformBranding";
import {
  isAsiMarketingHost,
  marketingOverviewPath,
  ASI_PRODUCT_PATHS,
} from "features/asi/marketing/asiMarketingRouting";
import {
  CLINICALHUD_PRODUCT_PATHS,
  isClinicalHudMarketingHost,
} from "features/clinicalHud/marketing/clinicalHudMarketingRouting";
import {
  peekStashedAuthNextPath,
  stashAuthNextPath,
} from "components/auth/oauthSignIn";
import { useMsi3SignedInLanding } from "features/msi3Teaching/useMsi3SignedInLanding";

/**
 * AuthPage - Unified sign-in / sign-up page (email, Google, Apple).
 *
 * Supports ?role=patient|provider|facility param to pre-select account type.
 * Supports ?next=/asi/... (sanitized in-app path) for marketing auth bridges —
 * same pattern as DpAuthPage / VoxPontAuthPage.
 * When fully signed in (role, onboarding done, email verified), redirects to
 * `next` when present, otherwise dashboard, or in prod to onboarding if MFA is
 * not yet enrolled. On the msi3 host (no explicit next), faculty land on
 * /msi3/faculty/cases and everyone else on /clinical-hud-5.
 */
const AuthPage = () => {
  const { user, userData, userLoading, tokenClaims } = useAuth();
  const { isEnrolled, sessionMfaLoading } = useMfaStatus();
  const [searchParams] = useSearchParams();
  const preselectedRole = searchParams.get("role");
  // Product origin (e.g. "voxpont") from a branded entry point. Stashed in
  // localStorage so it survives the email-verification round-trip and the
  // navigation to /onboarding (which both strip query params), mirroring how
  // pendingSignupRole is preserved. Read + cleared by Onboarding.
  const productOrigin = searchParams.get("product");
  const queryNext = sanitizeInAppReturnPath(searchParams.get("next"));
  const stashedNext = sanitizeInAppReturnPath(peekStashedAuthNextPath());
  const hasExplicitNext = Boolean(queryNext || stashedNext);
  const msi3Landing = useMsi3SignedInLanding({ enabled: !hasExplicitNext });
  const asiHost = isAsiMarketingHost();
  const defaultPostAuthRoute = isClinicalHudMarketingHost()
    ? CLINICALHUD_PRODUCT_PATHS.hud4
    : asiHost && !hasExplicitNext
      ? ASI_PRODUCT_PATHS.teaching
      : "/dashboard";
  const postAuthRoute =
    queryNext ||
    stashedNext ||
    (msi3Landing.active && msi3Landing.path) ||
    defaultPostAuthRoute;
  const clinicalHudHost = isClinicalHudMarketingHost();
  const branding = getPlatformBranding();
  // TODO(owner): replace placeholder favicon with a real ASI wordmark when supplied.
  const logoSrc = asiHost ? "/brand/asi/favicon.svg" : logoIcon;
  const logoAlt = branding.name;
  const homeFooterTo = asiHost ? marketingOverviewPath() : "/";
  const homeFooterLabel = asiHost
    ? `Back to ${branding.name}`
    : clinicalHudHost
      ? "Back to ClinicalHUD"
      : "Back to CareMap";

  const signupInProgressRef = useRef(false);
  const inviteAcceptingRef = useRef(false);
  const [inviteAccepting, setInviteAccepting] = useState(false);
  const [inviteTokenCleared, setInviteTokenCleared] = useState(false);
  const [invitePrefilledEmail, setInvitePrefilledEmail] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(
    Boolean(sessionStorage.getItem("pendingOrganizationInviteToken")),
  );
  const [emailVerifiedRefreshLoading, setEmailVerifiedRefreshLoading] =
    useState(false);
  const [emailVerifiedOverride, setEmailVerifiedOverride] = useState(null);
  const [isOnSuccessStep, setIsOnSuccessStep] = useState(false);
  const [institutionGateDone, setInstitutionGateDone] = useState(false);
  const pendingInviteToken = !inviteTokenCleared
    ? sessionStorage.getItem("pendingOrganizationInviteToken")
    : null;
  const isFacilityCardUser = hasFacilityCardClaim(tokenClaims);
  const effectiveEmailVerified =
    isFacilityCardUser ||
    isDevAuthFirestoreUser(userData) ||
    (emailVerifiedOverride === null
      ? !!user?.emailVerified
      : emailVerifiedOverride);

  // Marketing CTAs pass ?role=patient|provider|facility. Persist so Onboarding
  // still knows the intended account type after email-verify / Navigate strips
  // the query string (same pattern as ShareLandingPage).
  useEffect(() => {
    if (
      preselectedRole === "patient" ||
      preselectedRole === "provider" ||
      preselectedRole === "facility"
    ) {
      localStorage.setItem("pendingSignupRole", preselectedRole);
    }
  }, [preselectedRole]);

  useEffect(() => {
    if (productOrigin === "voxpont") {
      localStorage.setItem("pendingProductOrigin", "voxpont");
    }
  }, [productOrigin]);

  // Keep ?next= across an iOS OAuth redirect that may return without the query.
  useEffect(() => {
    if (queryNext) {
      stashAuthNextPath(queryNext);
      return;
    }
    if (isClinicalHudMarketingHost()) {
      stashAuthNextPath(CLINICALHUD_PRODUCT_PATHS.hud4);
    }
  }, [queryNext]);

  useEffect(() => {
    let cancelled = false;

    const refreshEmailVerified = async () => {
      if (!user || invitePrefilledEmail) {
        setEmailVerifiedOverride(null);
        return;
      }

      if (user.emailVerified) {
        setEmailVerifiedOverride(true);
        return;
      }

      setEmailVerifiedRefreshLoading(true);
      try {
        await firebase.auth().currentUser?.reload();
        if (cancelled) return;
        setEmailVerifiedOverride(!!firebase.auth().currentUser?.emailVerified);
      } catch (err) {
        if (cancelled) return;
        setEmailVerifiedOverride(!!user.emailVerified);
      } finally {
        if (!cancelled) setEmailVerifiedRefreshLoading(false);
      }
    };

    refreshEmailVerified();
    return () => {
      cancelled = true;
    };
  }, [user, invitePrefilledEmail]);

  const acceptInviteForCurrentUser = async () => {
    if (!pendingInviteToken) return;
    const acceptInvitation = firebase
      .functions()
      .httpsCallable("acceptInvitation");
    try {
      await acceptInvitation({ token: pendingInviteToken });
      sessionStorage.removeItem("pendingOrganizationInviteToken");
      setInviteTokenCleared(true);
    } catch (err) {
      const code = err?.code || "";
      const message = (err?.message || "").toLowerCase();
      const isTerminal =
        code.includes("already-exists") ||
        code.includes("permission-denied") ||
        code.includes("not-found") ||
        (code.includes("failed-precondition") &&
          (message.includes("accepted") ||
            message.includes("revoked") ||
            message.includes("expired") ||
            message.includes("different email") ||
            message.includes("organization")));
      if (isTerminal) {
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        setInviteTokenCleared(true);
      } else {
        throw err;
      }
    }
  };

  useEffect(() => {
    const loadInviteContext = async () => {
      if (!pendingInviteToken || user) {
        setInviteLoading(false);
        return;
      }
      setInviteLoading(true);
      try {
        const getInvitation = firebase
          .functions()
          .httpsCallable("getInvitation");
        const result = await getInvitation({ token: pendingInviteToken });
        const invitedEmail = result?.data?.invitation?.email || null;
        if (invitedEmail) setInvitePrefilledEmail(invitedEmail);
      } catch (err) {
        // Token is stale/invalid; clear it so auth works normally.
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        setInviteTokenCleared(true);
      } finally {
        setInviteLoading(false);
      }
    };

    loadInviteContext();
  }, [pendingInviteToken, user]);

  useEffect(() => {
    const acceptPendingInvite = async () => {
      if (!pendingInviteToken || !user || !userData) return;
      if (inviteAcceptingRef.current) return;

      // Nothing to do once user is already in an org.
      if (userData.organizationId) {
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        setInviteTokenCleared(true);
        return;
      }

      // Wait until account setup is done and role is set.
      if (userData.onboarding || userData.role !== "professional") return;

      inviteAcceptingRef.current = true;
      setInviteAccepting(true);
      try {
        await acceptInviteForCurrentUser();
      } catch (err) {
        // Keep token for retry path below.
      } finally {
        inviteAcceptingRef.current = false;
        setInviteAccepting(false);
      }
    };

    acceptPendingInvite();
  }, [pendingInviteToken, user, userData]);

  const fullySignedIn =
    user &&
    userData?.role &&
    !userData?.onboarding &&
    !needsMfaEnrollment({
      user,
      isEnrolled,
      skipUi: shouldSkipMfaEnrollmentUi(),
      claims: tokenClaims,
    }) &&
    effectiveEmailVerified &&
    !signupInProgressRef.current;

  if (fullySignedIn && institutionGateDone) {
    if (msi3Landing.active && msi3Landing.loading) {
      return <Loading page />;
    }
    return <Navigate to={postAuthRoute} replace />;
  }

  if (fullySignedIn && !institutionGateDone) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 4,
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt={logoAlt}
          sx={{ height: 48, mb: 2 }}
        />
        <InstitutionSelector onComplete={() => setInstitutionGateDone(true)} />
      </Box>
    );
  }

  const shouldShowLoading =
    userLoading ||
    inviteLoading ||
    inviteAccepting ||
    (user && userData === undefined) || // userData not yet fetched from Firestore
    (user && (sessionMfaLoading || emailVerifiedRefreshLoading));

  if (shouldShowLoading) {
    return <Loading page />;
  }

  const shouldRequireEmailVerification =
    user &&
    !effectiveEmailVerified &&
    !invitePrefilledEmail &&
    !signupInProgressRef.current;

  const initialStep = shouldRequireEmailVerification
    ? STEPS.EMAIL_VERIFY
    : undefined;

  if (user && !shouldRequireEmailVerification && !signupInProgressRef.current) {
    return <Navigate to="/onboarding" />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: 4,
      }}
    >
      {isOnSuccessStep ? (
        <Box
          component="img"
          src={logoSrc}
          alt={logoAlt}
          sx={{ height: 48, mb: 2 }}
        />
      ) : (
        <Link component={RouterLink} to="/">
          <Box
            component="img"
            src={logoSrc}
            alt={logoAlt}
            sx={{ height: 48, mb: 2 }}
          />
        </Link>
      )}

      <AuthFlow
        preselectedRole={preselectedRole}
        prefilledEmail={
          invitePrefilledEmail ||
          (shouldRequireEmailVerification ? user?.email : null)
        }
        disableOAuth={!!invitePrefilledEmail}
        skipEmailVerification={!!invitePrefilledEmail}
        allowSignup={false}
        initialStep={initialStep}
        onSignupStart={() => {
          signupInProgressRef.current = true;
        }}
        onSuccess={() => {
          // Stay on /auth until userData hydrates. Navigating to /dashboard from
          // AuthFlow races AuthContext and bounces unsigned iOS sessions back here.
        }}
        onSuccessStep={setIsOnSuccessStep}
      />

      {!isOnSuccessStep && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, mb: 4, textAlign: "center" }}
        >
          <Link
            component={RouterLink}
            to={homeFooterTo}
            color="inherit"
            underline="hover"
          >
            {homeFooterLabel}
          </Link>
        </Typography>
      )}
    </Box>
  );
};

export default AuthPage;
